// Anchor names have TWO namespaces, and reading them is one job — DECISION-073 item 3.
//
// D-073 (2026-09-18) split anchor names by SOURCE:
//
//   <KIND>_ANCHOR_NN            Content's. Authored in the shell GLB. Carries a merchant-facing
//                               ordinal, because a shop with three longboxes has a "Long Box 2".
//   <KIND>_ANCHOR_INST_<id>     The gateway's, minted for a free placement. The id is identity, not
//                               a label — nobody editing a shop should ever be shown it.
//
// THE DEFECT THIS EXISTS TO PREVENT IS A SILENT ONE. Three readers each carried their own copy of
// `/_ANCHOR(_\d+)?$/` (studio-v1/viewport.js, ws3/_ghost-controller.js, stage.js's label stem). An
// `_INST_` name matches none of them, so it falls through as "not an anchor" or as a raw stem — the
// merchant sees "Wall Shelf Anchor Inst A1b2c3d4" and the shell scan skips the node entirely.
// Nothing throws. Three regexes in three files is also three chances to fix two of them.
//
// So the pattern lives HERE, once, and the three readers ask this module instead of matching. Same
// reasoning as the mode-chrome selector lists: when a rule is duplicated, the duplicate is where the
// drift goes.
//
// ⚠️ `_INST_` IS CHECKED BEFORE THE ORDINAL, and the order is load-bearing: an id is hex, so
// `..._ANCHOR_INST_12345678` would satisfy a lazy "ends in digits" reading and be mistaken for
// ordinal 12345678. Anchored, explicit, and tested both ways (test-anchor-name.mjs).

/** `<KIND>_ANCHOR_INST_<id>` — a gateway-minted free placement. Id is opaque; never shown. */
const INSTANCE_RE = /^(.*?)_ANCHOR_INST_([0-9A-Z]+)$/i;
/** `<KIND>_ANCHOR` or `<KIND>_ANCHOR_NN` — Content-authored, in the shell GLB. */
const SHELL_RE = /^(.*?)_ANCHOR(?:_(\d+))?$/i;

/**
 * Parse an anchor name into the only three things a reader ever wants.
 * Returns null when the string is not an anchor name at all — callers that used to ask
 * "does this match?" ask "did this parse?".
 *
 * @returns {{kind: string, ordinal: number|null, instanceId: string|null}|null}
 *   kind       — the raw stem, e.g. "WALL_SHELF" (still upper/underscored; label formatting is below)
 *   ordinal    — Content's NN when present; null for every instance name
 *   instanceId — the minted id for an `_INST_` name; null for a shell anchor
 */
export function parseAnchorName(name) {
	const s = String(name || "");
	if (!s) return null;
	const inst = s.match(INSTANCE_RE);
	if (inst) return { kind: inst[1], ordinal: null, instanceId: inst[2] };
	const shell = s.match(SHELL_RE);
	if (shell) return { kind: shell[1], ordinal: shell[2] ? parseInt(shell[2], 10) : null, instanceId: null };
	return null;
}

/** True for any anchor name in EITHER namespace. Replaces the three `_ANCHOR(_\d+)?$` tests. */
export function isAnchorName(name) {
	return parseAnchorName(name) !== null;
}

/**
 * The merchant-facing label. `LONGBOX_ANCHOR_01` → "Longbox 1" · `WALL_SHELF_ANCHOR` → "Wall Shelf"
 * · `WALL_SHELF_ANCHOR_INST_A1B2C3D4` → "Wall Shelf".
 *
 * An instance gets NO number, deliberately. D-073: "identity and label are separate things" — the
 * only number we could show is the mint id, which is not an ordinal and means nothing to a merchant.
 * Two free-placed wall shelves therefore read alike, which is honest: nothing in the shop
 * distinguishes them except where they are, and that is what the merchant is looking at.
 */
export function fixtureLabelFor(name, fallback = "another fixture") {
	const a = parseAnchorName(name);
	if (!a) return name ? titleCase(String(name).replace(/_/g, " ").toLowerCase()) : fallback;
	const words = titleCase(a.kind.replace(/_/g, " ").toLowerCase());
	return a.ordinal !== null ? `${words} ${a.ordinal}` : words;
}

function titleCase(s) {
	return s.replace(/\b\w/g, (c) => c.toUpperCase());
}
